import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "../../../utils/request";

// Convert a UTC ISO string to ISO 8601 with the timezone offset of the given IANA timezone
// e.g. "2025-12-19T17:00:00.000Z" + "America/Los_Angeles" → "2025-12-19T09:00:00-08:00"
const toOffsetISO = (utcISOString, timeZone) => {
  const date = new Date(utcISOString);

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value;
  const localISO = `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`;

  // Derive offset by comparing local wall-clock time against UTC
  const offsetMs = new Date(localISO + "Z") - date;
  const offsetMins = Math.round(offsetMs / 60000);
  const sign = offsetMins >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMins);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");

  return `${localISO}${sign}${hh}:${mm}`;
};

// Async thunk for fetching booking config (company info) using hashId
export const fetchBookingConfig = createAsyncThunk(
  "publicClientBooking/fetchBookingConfig",
  async (hashId, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/page-info/?scheduling_hash=${hashId}`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.error ||
          error?.message ||
          "Failed to fetch booking configuration"
      );
    }
  }
);

// Async thunk for refetching duration when user manually changes square footage in Step 2
export const fetchDuration = createAsyncThunk(
  "publicClientBooking/fetchDuration",
  async ({ schedulingHash, sqft }, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/duration/?scheduling_hash=${schedulingHash}&sqft=${sqft}`,
        { method: "GET" }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch duration");
    }
  }
);

// Async thunk for fetching property details and estimated duration
// Called when user clicks "Next" in Step 1
export const fetchPropertyDetails = createAsyncThunk(
  "publicClientBooking/fetchPropertyDetails",
  async ({ schedulingHash, address }, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/property-details/`,
        {
          method: "POST",
          body: JSON.stringify({
            scheduling_hash: schedulingHash,
            address: address,
          }),
        }
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch property details"
      );
    }
  }
);

// Async thunk for fetching booking data with availability
// Called when user clicks "Next" in Step 2
export const fetchBookingData = createAsyncThunk(
  "publicClientBooking/fetchBookingData",
  async ({ schedulingHash, sqft, clientTimezone }, { rejectWithValue }) => {
    try {
      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/profile/booking-data/`,
        {
          method: "POST",
          body: JSON.stringify({
            scheduling_hash: schedulingHash,
            sqft: sqft,
            client_timezone: clientTimezone,
          }),
        }
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch booking data");
    }
  }
);

// Async thunk for confirming booking
// Called when user clicks "Confirm Booking" in Step 4
export const confirmBooking = createAsyncThunk(
  "publicClientBooking/confirmBooking",
  async (schedulingHash, { getState, rejectWithValue }) => {
    try {
      const state = getState().publicClientBooking;
      const {
        contactForm,
        agentForm,
        showAgentInfo,
        propertyDetailsForm,
        selectedTimeslot,
        selectedTimezone,
        idempotencyKey,
        notes,
      } = state;

      const body = {
        scheduling_hash: schedulingHash,
        start_time: toOffsetISO(selectedTimeslot.start_time, selectedTimezone),
        end_time: toOffsetISO(selectedTimeslot.end_time, selectedTimezone),
        idempotency_key: idempotencyKey,
        ...(notes?.trim() && { notes: notes.trim() }),

        client_name: contactForm.name,
        client_email: contactForm.email,
        client_phone: contactForm.phone,

        address_line1: contactForm.address1,
        ...(contactForm.address2 && { address_line2: contactForm.address2 }),
        city: contactForm.city,
        state: contactForm.state,
        zip_code: contactForm.postalCode,

        ...(propertyDetailsForm.squareFeet && {
          square_feet: Number(propertyDetailsForm.squareFeet),
        }),
        ...(propertyDetailsForm.yearBuilt && {
          year_built: Number(propertyDetailsForm.yearBuilt),
        }),
        ...(propertyDetailsForm.numberOfStories && {
          stories: Number(propertyDetailsForm.numberOfStories),
        }),
        ...(propertyDetailsForm.houseType && {
          house_type: propertyDetailsForm.houseType,
        }),
        ...(propertyDetailsForm.parcelNumber && {
          apn: propertyDetailsForm.parcelNumber,
        }),

        ...(showAgentInfo &&
          agentForm.agentName && { agent_name: agentForm.agentName }),
        ...(showAgentInfo &&
          agentForm.agentPhone && { agent_phone: agentForm.agentPhone }),
        ...(showAgentInfo &&
          agentForm.agentEmail && { agent_email: agentForm.agentEmail }),
      };

      const data = await request(
        `${process.env.REACT_APP_API_URL}/scheduling/booking/confirm/`,
        {
          method: "POST",
          body: JSON.stringify(body),
        }
      );
      return data;
    } catch (error) {
      const isLinkError =
        error?.error === "Inspector's scheduling is not active";
      return rejectWithValue({
        type: isLinkError ? "link-error" : "failure",
        message: error?.error || error?.message || "Failed to confirm booking",
      });
    }
  }
);

const initialState = {
  // Company info (fetched using hashId)
  companyInfo: null,
  inspectorInfo: null,
  companyLoading: false,
  companyError: null,
  maxBookingHorizonDays: 60,

  // Stepper
  currentStep: 0,

  // Step 1: Contact & Address form
  contactForm: {
    name: "",
    phone: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
  },
  showAgentInfo: false,
  agentForm: {
    agentName: "",
    agentPhone: "",
    agentEmail: "",
  },

  // Form validation errors
  formErrors: {},

  // Step 2: Property Details
  propertyDetailsForm: {
    squareFeet: "",
    yearBuilt: "",
    numberOfStories: "",
    houseType: "",
    parcelNumber: "",
  },
  propertyLoading: false,
  propertyError: null,
  propertyDataFound: null, // null = not fetched yet, true = found, false = not found

  // Estimated inspection duration (from property details API in Step 1, or duration API in Step 2)
  estimatedDurationMinutes: null,
  durationLoading: false,

  // Step 3: Select Date & Time
  selectedDate: null,
  selectedTimeslot: null,
  selectedTimezone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles",

  // Booking data (from booking-data API in Step 2)
  durationMinutes: null, // Final calculated duration from booking-data API
  availability: null, // null = not fetched, {} = no slots, {date: slots} = available
  schedulingEnabled: null, // null = not checked, true = enabled, false = not enabled
  bookingDataLoading: false,
  bookingDataError: null,

  // Step 4: Confirm Booking
  notes: "",
  idempotencyKey: null, // Generated when user proceeds from Step 3; reused on retries
  confirmLoading: false,
  confirmError: null,
  confirmResult: null,
  bookingOutcome: null, // null | 'success' | 'failure' | 'link-error'
};

export const publicClientBookingSlice = createSlice({
  name: "publicClientBooking",
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setContactForm: (state, action) => {
      state.contactForm = { ...state.contactForm, ...action.payload };
    },
    setShowAgentInfo: (state, action) => {
      state.showAgentInfo = action.payload;
    },
    setAgentForm: (state, action) => {
      state.agentForm = { ...state.agentForm, ...action.payload };
    },
    setFormErrors: (state, action) => {
      state.formErrors = action.payload;
    },
    clearFormError: (state, action) => {
      const { [action.payload]: _, ...rest } = state.formErrors;
      state.formErrors = rest;
    },
    resetForm: (state) => {
      state.contactForm = initialState.contactForm;
      state.agentForm = initialState.agentForm;
      state.showAgentInfo = false;
      state.formErrors = {};
      state.currentStep = 0;
      state.propertyDetailsForm = initialState.propertyDetailsForm;
      state.propertyDataFound = null;
      state.estimatedDurationMinutes = null;
      // Reset Step 3
      state.selectedDate = null;
      state.selectedTimeslot = null;
      state.availability = null;
      state.durationMinutes = null;
      state.schedulingEnabled = null;
      state.idempotencyKey = null;
      state.notes = "";
      state.confirmResult = null;
      state.confirmError = null;
      state.bookingOutcome = null;
    },
    setPropertyDetailsForm: (state, action) => {
      state.propertyDetailsForm = {
        ...state.propertyDetailsForm,
        ...action.payload,
      };
    },
    clearPropertyDetails: (state) => {
      state.propertyDetailsForm = initialState.propertyDetailsForm;
      state.propertyDataFound = null;
      state.estimatedDurationMinutes = null;
    },
    // Step 3: Date & Time selection
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedTimeslot: (state, action) => {
      state.selectedTimeslot = action.payload;
    },
    setSelectedTimezone: (state, action) => {
      state.selectedTimezone = action.payload;
    },
    clearDateTimeSelection: (state) => {
      state.selectedDate = null;
      state.selectedTimeslot = null;
    },
    generateIdempotencyKey: (state) => {
      state.idempotencyKey = crypto.randomUUID();
    },
    setNotes: (state, action) => {
      state.notes = action.payload;
    },
    resetBookingOutcome: (state) => {
      state.bookingOutcome = null;
      state.confirmError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Booking config (company info)
      .addCase(fetchBookingConfig.pending, (state) => {
        state.companyLoading = true;
        state.companyError = null;
      })
      .addCase(fetchBookingConfig.fulfilled, (state, action) => {
        state.companyLoading = false;
        state.companyInfo = {
          name: action.payload.company?.name || null,
          logo: action.payload.company?.logo_url || null,
        };
        state.inspectorInfo = action.payload.inspector || null;
        state.maxBookingHorizonDays =
          action.payload.max_booking_horizon_days ?? 60;
      })
      .addCase(fetchBookingConfig.rejected, (state, action) => {
        state.companyLoading = false;
        state.companyError = action.payload || action.error.message;
      })
      // Duration refetch (called when user manually changes sqft in Step 2)
      .addCase(fetchDuration.pending, (state) => {
        state.durationLoading = true;
      })
      .addCase(fetchDuration.fulfilled, (state, action) => {
        state.durationLoading = false;
        state.estimatedDurationMinutes = action.payload.duration_minutes;
      })
      .addCase(fetchDuration.rejected, (state) => {
        state.durationLoading = false;
      })
      // Property details (called when clicking Next in Step 1)
      .addCase(fetchPropertyDetails.pending, (state) => {
        state.propertyLoading = true;
        state.propertyError = null;
      })
      .addCase(fetchPropertyDetails.fulfilled, (state, action) => {
        state.propertyLoading = false;
        const data = action.payload;

        // Check if property data was found
        const hasData = data.square_feet !== null;
        state.propertyDataFound = hasData;

        if (hasData) {
          state.propertyDetailsForm = {
            squareFeet: data.square_feet || "",
            yearBuilt: data.year_built || "",
            numberOfStories: data.stories || "",
            houseType: data.house_type || "",
            parcelNumber: data.parcel_number || "",
          };
        }

        // Set estimated duration (may be null)
        state.estimatedDurationMinutes = data.estimated_duration_minutes;

        // Move to Step 2
        state.currentStep = 1;
      })
      .addCase(fetchPropertyDetails.rejected, (state, action) => {
        state.propertyLoading = false;
        state.propertyError = action.payload || action.error.message;
        state.propertyDataFound = false;
        // Still move to Step 2 even on error
        state.currentStep = 1;
      })
      // Booking data with availability (called when clicking Next in Step 2)
      .addCase(fetchBookingData.pending, (state) => {
        state.bookingDataLoading = true;
        state.bookingDataError = null;
      })
      .addCase(fetchBookingData.fulfilled, (state, action) => {
        state.bookingDataLoading = false;
        const data = action.payload;

        // Set duration from this API (more accurate than estimate)
        state.durationMinutes = data.duration_minutes;

        // Set timezone from response
        state.selectedTimezone = data.client_timezone;

        // Check if scheduling service is enabled
        if (data.availability === null) {
          state.schedulingEnabled = false;
          state.availability = null;
        } else {
          state.schedulingEnabled = true;
          state.availability = data.availability;
        }

        // Move to Step 3
        state.currentStep = 2;
      })
      .addCase(fetchBookingData.rejected, (state, action) => {
        state.bookingDataLoading = false;
        state.bookingDataError = action.payload || action.error.message;
      })
      // Confirm booking
      .addCase(confirmBooking.pending, (state) => {
        state.confirmLoading = true;
        state.confirmError = null;
      })
      .addCase(confirmBooking.fulfilled, (state, action) => {
        state.confirmLoading = false;
        state.confirmResult = action.payload;
        state.bookingOutcome = "success";
      })
      .addCase(confirmBooking.rejected, (state, action) => {
        state.confirmLoading = false;
        state.confirmError = action.payload?.message || action.error.message;
        state.bookingOutcome = action.payload?.type || "failure";
      });
  },
});

export const {
  setCurrentStep,
  setContactForm,
  setShowAgentInfo,
  setAgentForm,
  setFormErrors,
  clearFormError,
  resetForm,
  setPropertyDetailsForm,
  clearPropertyDetails,
  setSelectedDate,
  setSelectedTimeslot,
  setSelectedTimezone,
  clearDateTimeSelection,
  generateIdempotencyKey,
  setNotes,
  resetBookingOutcome,
} = publicClientBookingSlice.actions;

export default publicClientBookingSlice.reducer;
